import { useState, useEffect, useCallback, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, ChevronLeft, ChevronRight, ShoppingBag } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { useFavorites } from "@/context/FavoritesContext";
import axios from "axios";

import { API, BACKEND_URL } from "../apiConfig";
import LOGO_URL from "../img/sapphire.svg";

// ─── Storage key para el estado de búsqueda ────────────────
const SEARCH_STATE_KEY = "sapphire_search_state";

// ─── Función para guardar el estado de búsqueda ───────────
const saveSearchState = (state) => {
  sessionStorage.setItem(SEARCH_STATE_KEY, JSON.stringify(state));
};

const clearSearchState = () => {
  sessionStorage.removeItem(SEARCH_STATE_KEY);
};

// ─── Función para restaurar el estado de búsqueda ────────
const loadSearchState = () => {
  try {
    const saved = sessionStorage.getItem(SEARCH_STATE_KEY);
    return saved ? JSON.parse(saved) : null;
  } catch {
    return null;
  }
};

// ─── Perfume Card Component ───────────────────────────────────
const PerfumeCard = ({ perfume, index, onOpenDetail }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const { toggleFavorite, isFavorite } = useFavorites();
  const liked = isFavorite(perfume.id);

  const placeholderImage = "../images/photo-1618436623662-6043c7cb58df.jpeg";

  const handleFav = (e) => {
    e.preventDefault();
    e.stopPropagation();
    toggleFavorite(perfume);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.05 }}
    >
      <Link
        to={`/perfume/${perfume.id}`}
        onClick={() => onOpenDetail(perfume.id)}
        className="perfume-card block group"
        data-testid={`perfume-card-${index}`}
        data-perfume-id={perfume.id}
      >
        <div className="bg-white rounded-3xl shadow-[0_8px_32px_rgba(0,0,0,0.04)] border border-slate-100 hover:shadow-[0_16px_48px_rgba(0,0,0,0.08)] transition-all duration-500 overflow-hidden relative">
          {/* Image Container */}
          <div className="bg-[#FFFFFF] p-6 relative overflow-hidden aspect-[4/5] flex items-center justify-center">
            {/* Brand Type Pill */}
            {perfume.tipo_marca && (
              <Badge 
                className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm text-slate-800 text-xs font-semibold tracking-wider px-3 py-1 rounded-full shadow-sm z-10 pointer-events-none"
                data-testid={`brand-type-pill-${index}`}
              >
                {perfume.tipo_marca}
              </Badge>
            )}

            {/* Bag Button */}
            <button
              onClick={handleFav}
              className="absolute top-4 right-4 z-10 w-11 h-11 rounded-full bg-white/90 backdrop-blur-sm shadow-md flex items-center justify-center transition-transform duration-200 hover:scale-110 active:scale-95"
              data-testid={`fav-btn-${index}`}
            >
              <ShoppingBag
                className={`w-6 h-6 transition-colors duration-200 ${liked ? "fill-white-500 text-rose-500" : "text-slate-400"}`}
                strokeWidth={1.5}
              />
            </button>
            
            {!imageLoaded && !imageError && (
              <Skeleton className="absolute inset-6 rounded-2xl skeleton-shimmer" />
            )}
            
            <img
              src={imageError ? placeholderImage : (perfume.imagen_url || placeholderImage)}
              alt={perfume.nombre}
              className={`perfume-card-image max-h-full max-w-full object-contain transition-opacity duration-300 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
              onLoad={() => setImageLoaded(true)}
              onError={() => { setImageError(true); setImageLoaded(true); }}
            />
          </div>
          
          {/* Content */}
          <div className="p-6">
            <div className="flex items-center gap-2 mb-1">
              <p className="text-xs font-semibold tracking-wider text-slate-500 uppercase">
                {perfume.marca}
              </p>
              {perfume.novedad && (
                <Badge className="bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-2 py-0.5 text-[10px] font-semibold pointer-events-none">
                  Novedad
                </Badge>
              )}
            </div>
            <h3 className="font-serif text-lg text-slate-900 leading-tight line-clamp-2 group-hover:text-slate-700 transition-colors">
              {perfume.nombre}
            </h3>
            <p className="text-sm text-slate-500 mt-2 truncate">
              {perfume.familia_olfativa}
            </p>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

// ─── Filter Pill Component ────────────────────────────────────
const FilterPill = ({ label, active, onClick }) => (
  <button
    onClick={onClick}
    className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-300 ${
      active
        ? "bg-slate-900 text-white shadow-lg shadow-slate-900/20"
        : "bg-white text-slate-700 border border-slate-200 hover:bg-slate-50"
    }`}
  >
    {label}
  </button>
);

// ─── Main Home Component ──────────────────────────────────────
const Home = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [perfumes, setPerfumes] = useState([]);
  const [filters, setFilters] = useState({ generos: [], tipos_marca: [], marcas: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedGenero, setSelectedGenero] = useState(null);
  const [selectedTipoMarca, setSelectedTipoMarca] = useState(null);
  const [showNovedades, setShowNovedades] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [catalogStateReady, setCatalogStateReady] = useState(false);
  const { getFavoritesCount } = useFavorites();
  const favCount = getFavoritesCount();
  const gridRef = useRef(null);
  const restoredStateRef = useRef(false);
  const pendingScrollRestoreRef = useRef(false);

  // Restore catalog state when returning from a perfume detail page.
  useEffect(() => {
    if (restoredStateRef.current) return;
    restoredStateRef.current = true;

    const saved = loadSearchState();
    const g = searchParams.get("genero");
    const t = searchParams.get("tipo_marca");
    const n = searchParams.get("novedad");
    const hasUrlFilters = Boolean(g || t || n);

    if (saved && !hasUrlFilters) {
      setSelectedGenero(saved.selectedGenero || null);
      setSelectedTipoMarca(saved.selectedTipoMarca || null);
      setShowNovedades(Boolean(saved.showNovedades));
      setSearchQuery(saved.searchQuery || "");
      setPage(saved.page || 1);
      pendingScrollRestoreRef.current = true;
      setCatalogStateReady(true);
      return;
    }

    if (hasUrlFilters) clearSearchState();
    if (g) setSelectedGenero(g);
    if (t) setSelectedTipoMarca(t);
    if (n === "true") setShowNovedades(true);
    if (hasUrlFilters) setSearchParams({}, { replace: true });
    setCatalogStateReady(true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll after data has rendered, otherwise the browser restores too early.
  useEffect(() => {
    if (!pendingScrollRestoreRef.current || loading || perfumes.length === 0) return;

    const saved = loadSearchState();
    if (!saved) return;

    requestAnimationFrame(() => {
      const card = gridRef.current?.querySelector(`[data-perfume-id="${saved.perfumeId}"]`);
      if (card) {
        card.scrollIntoView({ block: "center" });
      } else if (typeof saved.scrollPosition === "number") {
        window.scrollTo(0, saved.scrollPosition);
      }
      clearSearchState();
      pendingScrollRestoreRef.current = false;
    });
  }, [loading, perfumes]);

  const saveCatalogPosition = (perfumeId) => {
    saveSearchState({
      perfumeId,
      scrollPosition: window.scrollY,
      page,
      selectedGenero,
      selectedTipoMarca,
      showNovedades,
      searchQuery,
    });
  };

  const resetCatalogPosition = () => {
    if (pendingScrollRestoreRef.current) {
      pendingScrollRestoreRef.current = false;
      clearSearchState();
    }
  };

  // Fetch filters
  const fetchFilters = async () => {
    try {
      const response = await axios.get(`${API}/filters`);
      setFilters(response.data);
    } catch (error) {
      console.error("Error fetching filters:", error);
    }
  };

  // Fetch perfumes
 const fetchPerfumes = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("page", page);
      params.append("page_size", 20);
      if (selectedGenero) params.append("genero", selectedGenero);
      if (selectedTipoMarca) params.append("tipo_marca", selectedTipoMarca);
      if (showNovedades) params.append("novedad", "true");
      if (searchQuery) params.append("search", searchQuery);

      const response = await axios.get(`${API}/perfumes?${params.toString()}`);
      setPerfumes(response.data.perfumes);
      setTotalPages(response.data.total_pages);
      setTotal(response.data.total);
    } catch (error) {
      console.error("Error fetching perfumes:", error);
    } finally {
      setLoading(false);
    }
  }, [page, selectedGenero, selectedTipoMarca, showNovedades, searchQuery]);

  // Initial load
  useEffect(() => { fetchFilters(); }, []);
  useEffect(() => {
    if (catalogStateReady) fetchPerfumes();
  }, [catalogStateReady, fetchPerfumes]);
  useEffect(() => {
    if (!pendingScrollRestoreRef.current) setPage(1);
  }, [selectedGenero, selectedTipoMarca, showNovedades, searchQuery]);

 // ─── Funciones de Selección ────────────────────────────────
  // ─── Funciones de Selección (Faltantes) ──────────────────────
  const handleGeneroSelect = (genero) => {
    resetCatalogPosition();
    setSelectedGenero(genero);
    setShowNovedades(false); // Desactivar novedades si se elige un género
    setPage(1);
  };

  const handleTipoMarcaSelect = (tipo) => {
    resetCatalogPosition();
    setSelectedTipoMarca(tipo);
    setPage(1);
  };

  const toggleNovedades = () => {
    resetCatalogPosition();
    const estadoAnterior = showNovedades;
    setShowNovedades(!estadoAnterior);
    if (!estadoAnterior) {
      setSelectedGenero(null); // Desactivar género si se activan novedades
    }
    setPage(1);
  };

  const clearFilters = () => {
    resetCatalogPosition();
    setSelectedGenero(null);
    setSelectedTipoMarca(null);
    setShowNovedades(false);
    setSearchQuery("");
    setPage(1);
  };
  // Corregimos handleSearch para que no explote con el input
  const handleSearch = (valueOrEvent) => {
    resetCatalogPosition();
    if (valueOrEvent?.preventDefault) {
      valueOrEvent.preventDefault();
      fetchPerfumes();
    } else {
      setSearchQuery(valueOrEvent);
    }
  };

  //const hasActiveFilters = selectedGenero || selectedTipoMarca || showNovedades || searchQuery;
  const dynamicGenders = [...new Set(perfumes.map(p => p.genero))].filter(Boolean);
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-slate-200/50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-3 md:py-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between md:gap-4">
            <div className="flex items-center justify-between gap-4 md:contents">
            <Link to="/" className="flex items-center gap-2 flex-shrink-0">
              <img src={LOGO_URL} alt="Sapphire Spell" className="h-16 w-auto md:h-14" />
            </Link>

              <Link to="/wishlist" className="relative p-2 hover:bg-slate-100 rounded-lg transition-colors md:order-3">
                <ShoppingBag className="w-6 h-6 text-slate-700" />
                {favCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-rose-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                    {favCount}
                  </span>
                )}
              </Link>
            </div>

            <div className="w-full md:flex-1 md:max-w-2xl md:order-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Buscar por nombre, marca o familia olfativa..."
                  value={searchQuery}
                  // Solo actualizamos el estado, no llamamos a handleSearch aquí
                  onChange={(e) => handleSearch(e.target.value)}
                  className="w-full h-12 pl-10 pr-4 rounded-full border border-slate-200 bg-slate-50 text-base focus:bg-white focus:border-slate-300 md:h-10 md:text-sm"
                />
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Title */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-serif text-slate-900 mb-4">Descubre tu esencia</h1>
          <p className="text-lg text-slate-600">Explora nuestra colección de fragancias exclusivas</p>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-8">
            <p className="text-red-800">⚠️ {error}</p>
            <p className="text-red-700 text-sm mt-2">Backend URL: {BACKEND_URL}</p>
          </div>
        )}

        {/* Filters */}
        <div className="space-y-6 mb-12">
          {/* Género */}
          <div>
  <h3 className="text-sm font-semibold text-slate-900 mb-3">Género:</h3>
  <ScrollArea className="w-full">
    <div className="flex gap-2 pb-4">
      <FilterPill
        label="Todos"
        active={!selectedGenero && !showNovedades}
        onClick={() => {
          resetCatalogPosition();
          setSelectedGenero(null);
          setShowNovedades(false);
          setPage(1);
        }}
      />
      {/* Botón Novedades */}
      <FilterPill
        label="✨ Novedades"
        active={showNovedades}
        onClick={toggleNovedades}
      />
      {(filters.generos || []).map((genero) => (
        <FilterPill
          key={genero}
          label={genero}
          active={selectedGenero === genero}
          onClick={() => handleGeneroSelect(genero)}
        />
      ))}
    </div>
    <ScrollBar orientation="horizontal" />
  </ScrollArea>
</div>

          {/* Sección Tipo de Marca */}
            <div>
              <h3 className="text-sm font-semibold text-slate-900 mb-3">Tipo de marca:</h3>
              <ScrollArea className="w-full">
                <div className="flex gap-2 pb-4">
                  <FilterPill
                    label="Todas"
                    active={!selectedTipoMarca}
                    onClick={() => handleTipoMarcaSelect(null)}
                  />
                  {/* Usamos el objeto filters.tipos_marca para que sea dinámico */}
                  {(filters.tipos_marca || []).map((tipo) => (
                    <FilterPill
                      key={tipo}
                      label={tipo}
                      active={selectedTipoMarca === tipo}
                      onClick={() => handleTipoMarcaSelect(tipo)}
                    />
                  ))}
                </div>
                <ScrollBar orientation="horizontal" />
              </ScrollArea>
            </div>

          
        </div>

        {/* Perfumes Grid */}
        {loading && perfumes.length === 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <Skeleton key={i} className="aspect-[4/5] rounded-3xl" />
            ))}
          </div>
        ) : perfumes.length > 0 ? (
          <>
            <div ref={gridRef} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
              {perfumes.map((perfume, index) => (
              <PerfumeCard
                key={perfume.id}
                perfume={perfume}
                index={index}
                onOpenDetail={saveCatalogPosition}
              />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-4">
                <Button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  variant="outline"
                  size="sm"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm text-slate-600">
                  Página {page} de {totalPages}
                </span>
                <Button
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page === totalPages}
                  variant="outline"
                  size="sm"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-12">
            <p className="text-slate-600">No se encontraron perfumes</p>
          </div>
        )}
      </main>
     {/* Footer */}
      <footer className="bg-white border-t border-slate-100 py-8 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-slate-500 text-sm">© 2026 Sapphire Spell. Todos los derechos reservados.</p>
        </div>
      </footer>
      
    </div>
  );
};

export default Home;
